/**
 * Manufacturer contact roles. These drive PO email routing — see
 * backend/api/app/services/po_email.py:resolve_po_recipient and the import
 * pipeline at backend/api/app/scripts/import_purchasing_data.py.
 *
 * Currently only the PO routing and data@ roles (USA-1474) are user-pickable. Legacy free-form
 * values still in the DB (e.g. "purchasing", "primary", "po_last_resort") are
 * preserved on read via formatContactRole's fallback.
 */

export interface ContactRoleOption {
  value: string
  label: string
  description?: string
}

export const MANUFACTURER_CONTACT_ROLES: ContactRoleOption[] = [
  {
    value: 'po_primary',
    label: 'PO Primary',
    description: 'Receives purchase order emails (TO)',
  },
  {
    value: 'po_primary_cc',
    label: 'PO Primary CC',
    description: 'CC’d on purchase order emails',
  },
  {
    value: 'spec_credit',
    label: 'Spec Credit',
    description: 'Receives spec credit request emails (falls back to PO Primary if unset)',
  },
  {
    value: 'spec_credit_cc',
    label: 'Spec Credit CC',
    description: 'CC’d on spec credit request emails',
  },
  {
    value: 'po_backup',
    label: 'PO Backup',
    description: 'Backup PO recipient',
  },
  {
    value: 'po_backup_cc',
    label: 'PO Backup CC',
    description: 'CC’d on backup PO emails',
  },
  { value: 'accounting', label: 'Accounting', description: 'Invoice requests and payment emails' },
  { value: 'customer_service', label: 'Customer Service' },
  { value: 'service', label: 'Service', description: 'Warranty claims' },
  {
    value: 'data',
    label: 'Data',
    description: 'Receives data@ emails such as the weekly lead-time check (falls back to PO Primary if unset)',
  },
  {
    value: 'data_cc',
    label: 'Data CC',
    description: 'CC’d on data@ emails',
  },
]

const ROLE_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  MANUFACTURER_CONTACT_ROLES.map((r) => [r.value, r.label]),
)

/**
 * Format a role value for display. Falls back to title-casing the raw value
 * for legacy free-form entries that aren't in the canonical list.
 */
export function formatContactRole(role: string | null | undefined): string {
  if (!role) return ''
  if (ROLE_LABEL_BY_VALUE[role]) return ROLE_LABEL_BY_VALUE[role]
  return role
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}
